import React, { useContext, useEffect, useState, useRef } from 'react'
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Grid,
    InputAdornment,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import {
    AccountBalanceWallet,
    AccessTime,
    Add,
    CalendarMonth,
    FilterList,
    Search,
    TrendingUp,
    WarningAmber,
} from '@mui/icons-material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import moment from 'moment'
import { Helmet } from 'react-helmet'
import Context from '../../../contexts/Context'
import NoResultsAnimation from '../../../components/Shared/animations/NoResultsAnimation'
import PaginationGlobal from '../../../components/Shared/PaginationGlobal'
import Masks from '../../../components/Shared/Masks'
import KPICard from '../Dashboard/components/KPICard'
import ExportDropdownButton from '../../../components/Shared/ExportDropdownButton'
import { useNavigate } from 'react-router-dom'

const STATUS_OPTIONS = [
    { key: null, label: 'Todos', color: '#64748B' },
    { key: 'open', label: 'Em Aberto', color: '#3B82F6' },
    { key: 'pending', label: 'Pendente', color: '#F59E0B' },
    { key: 'processing', label: 'Processando', color: '#8B5CF6' },
    { key: 'paid', label: 'Pago', color: '#22C55E' },
    { key: 'overdue', label: 'Atrasado', color: '#EF4444' },
]

const getStatusChip = (statusKey) => {
    const found = STATUS_OPTIONS.find((s) => s.key === statusKey)
    if (!found) return null
    return (
        <Chip
            label={found.label}
            size="small"
            sx={{
                bgcolor: `${found.color}14`,
                color: found.color,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 26,
                border: `1px solid ${found.color}30`,
            }}
        />
    )
}

const ReceivablesList = () => {
    const navigate = useNavigate()
    const { user, invoiceListRefresh, setInvoiceListRefresh } = useContext(Context)
    const typingTimerRef = useRef(null)

    const [response, setResponse] = useState(null)
    const [summary, setSummary] = useState(null)
    const [activeStatus, setActiveStatus] = useState(null)
    const [search, setSearch] = useState(null)
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(moment().startOf('month').toDate())
    const [endDate, setEndDate] = useState(moment().endOf('month').toDate())
    const [filters, setFilters] = useState({
        unit__condo_id: getCondoId(),
        page_size: 50,
        page: 1,
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
    })

    function getInvoices() {
        const params = {
            due_date__gte: filters.startDate,
            due_date__lte: filters.endDate,
            page_size: filters.page_size,
            page: filters.page,
            unit__condo_id: getCondoId(),
            ordering: filters.ordering,
        }

        if (activeStatus) params.status = activeStatus
        if (search) params.search = search

        if (params.due_date__lte && params.due_date__gte) {
            api.get('receivables/', { params })
                .then((res) => setResponse(res.data))
                .catch((error) => console.log(error))
                .finally(() => {
                    setLoading(false)
                    setInvoiceListRefresh(false)
                })
        }
    }

    useEffect(() => {
        const summaryData = {
            condo: +getCondoId(),
            due_date__gte: filters.startDate,
            due_date__lte: filters.endDate,
        }
        api.post('analytics/receivables-summary/', summaryData)
            .then((res) => setSummary(res.data))
            .catch(() => {})
    }, [response])

    useEffect(() => {
        getInvoices()
        window.scrollTo(0, 0)
    }, [filters, activeStatus, search])

    useEffect(() => {
        if (invoiceListRefresh === true) getInvoices()
    }, [invoiceListRefresh])

    const kpis = summary?.kpis || {}
    const prevLabel = summary?.period?.previous
        ? `vs ${moment(summary.period.previous.start).format('DD/MM')} - ${moment(summary.period.previous.end).format('DD/MM')}`
        : 'vs período anterior'

    return (
        <>
            <Helmet>
                <title>Recebimentos - Bloco B3</title>
            </Helmet>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            Recebimentos
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <ExportDropdownButton
                                endpoint="invoice/export/"
                                buildParams={() => ({
                                    unit__condo_id: getCondoId(),
                                    due_date__gte: filters.startDate,
                                    due_date__lte: filters.endDate,
                                    ...(activeStatus ? { status: activeStatus } : {}),
                                    ...(search ? { search } : {}),
                                })}
                                defaultFilename="recebimentos_export"
                                buttonProps={{
                                    sx: {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        borderColor: '#E2E8F0',
                                        color: '#64748B',
                                        '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate('/financeiro/recebimentos/criar')}
                                sx={{
                                    bgcolor: '#3B82F6',
                                    '&:hover': { bgcolor: '#2563EB' },
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    px: 3,
                                }}
                            >
                                Novo Recebimento
                            </Button>
                        </Box>
                    </Box>

                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Total a Receber"
                                value={(kpis?.total_receivable?.value || 0) / 100}
                                variation={kpis?.total_receivable?.variation || 0}
                                variationLabel={prevLabel}
                                icon={<AccountBalanceWallet />}
                                color="#3B82F6"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Total Recebido"
                                value={(kpis?.total_received?.value || 0) / 100}
                                variation={kpis?.total_received?.variation || 0}
                                variationLabel={prevLabel}
                                icon={<TrendingUp />}
                                color="#22C55E"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Total Vencido"
                                value={(kpis?.total_overdue?.value || 0) / 100}
                                variation={kpis?.total_overdue?.variation || 0}
                                variationLabel={prevLabel}
                                icon={<WarningAmber />}
                                color="#EF4444"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Total Pendente"
                                value={(kpis?.total_pending?.value || 0) / 100}
                                variation={kpis?.total_pending?.variation || 0}
                                variationLabel={prevLabel}
                                icon={<AccessTime />}
                                color="#F59E0B"
                            />
                        </Grid>
                    </Grid>

                    {/* Filters Card */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            mb: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FilterList sx={{ color: '#94A3B8', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                                    Filtros
                                </Typography>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar por nome ou número..."
                                        variant="outlined"
                                        size="small"
                                        onKeyUp={(event) => {
                                            clearTimeout(typingTimerRef.current)
                                            typingTimerRef.current = setTimeout(() => setSearch(event.target.value), 800)
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search sx={{ color: '#94A3B8', fontSize: 20 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fff',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2.5}>
                                    <DesktopDatePicker
                                        label="De"
                                        value={startDate}
                                        onChange={(val) => {
                                            setStartDate(val)
                                            setFilters((prev) => ({
                                                ...prev,
                                                startDate: moment(val).format('YYYY-MM-DD'),
                                            }))
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                inputProps: { readOnly: true },
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2.5}>
                                    <DesktopDatePicker
                                        label="Até"
                                        value={endDate}
                                        onChange={(val) => {
                                            setEndDate(val)
                                            setFilters((prev) => ({
                                                ...prev,
                                                endDate: moment(val).format('YYYY-MM-DD'),
                                            }))
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                inputProps: { readOnly: true },
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Status Chips */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                {STATUS_OPTIONS.map((opt) => (
                                    <Chip
                                        key={opt.key ?? 'all'}
                                        label={opt.label}
                                        size="small"
                                        onClick={() => setActiveStatus(opt.key)}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            borderRadius: '8px',
                                            height: 32,
                                            px: 0.5,
                                            bgcolor: activeStatus === opt.key ? `${opt.color}14` : 'transparent',
                                            color: activeStatus === opt.key ? opt.color : '#64748B',
                                            border: activeStatus === opt.key ? `1.5px solid ${opt.color}` : '1.5px solid #E2E8F0',
                                            '&:hover': {
                                                bgcolor: `${opt.color}14`,
                                                borderColor: opt.color,
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Card>

                    {/* Table Card */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        {response?.results ? (
                            <TableContainer>
                                {response?.results?.length > 0 ? (
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                <TableCell sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Nº
                                                </TableCell>
                                                <TableCell sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Cliente
                                                </TableCell>
                                                <TableCell sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Vencimento
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Status
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Valor
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {response?.results?.map((object) => (
                                                <TableRow
                                                    key={object.id}
                                                    onClick={() => navigate(`/financeiro/recebimentos/${object.id}`)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.15s ease',
                                                        '&:hover': { bgcolor: '#F1F5F9' },
                                                        '& td': { borderBottom: '1px solid #F1F5F9' },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500, fontFamily: 'monospace' }}>
                                                            #{object.number}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                            {object.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <CalendarMonth sx={{ fontSize: 16, color: '#CBD5E1' }} />
                                                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                                                                {moment(object.due_date).format('DD/MM/YYYY')}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {getStatusChip(object?.status?.key)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                            {object?.total_paid > 0
                                                                ? Masks.money(object?.total_paid.toString())
                                                                : Masks.money(object?.total.toString())}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <NoResultsAnimation />
                                )}
                            </TableContainer>
                        ) : (
                            <Skeleton variant="rounded" height={500} animation="pulse" sx={{ m: 2 }} />
                        )}
                    </Card>

                    {response?.count > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <PaginationGlobal state={response} filters={filters} setFilters={setFilters} />
                        </Box>
                    )}
                </Container>
            </Box>
        </>
    )
}

export default ReceivablesList
